import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { DateTime as DateTimeLuxon } from 'luxon'
import { Dialog } from 'primereact/dialog'
import { TabPanel } from 'primereact/tabview'
import { RadioButton } from 'primereact/radiobutton'
import { Card } from 'primereact/card'

import {
  BadgeStatusEntrega,
  Btn,
  CustomInputTextArea,
  CustomTabView,
  ImageContainer,
  InputText,
  Loading,
  UploadFileBtn,
} from '../../../../components'
import { azul_escuro, vinho } from '../../../../assets/js/colors'
import { showMessageBox } from '../../../../store/actions/system.actions'
import { errorHandler } from '../../../../services/util/error.service'
import * as RetornoHttp from '../../../../services/api/https/deliveries/retorno.http'
import {
  getDelivery,
  getDeliveryTimeline,
  getMissingProductByDeliveryId,
} from '../../../../services/api/https/deliveries/delivery.http'
import { getClientByDeliveryId } from '../../../../services/api/https/registration/client.http'
import { urlUploadCanhoto } from '../../../../services/api/routes/receive-vehicle.routes'
import FaltaDataTable from '../../Entrega/Ocorrencias/Falta/FaltaDataTable'
import OcorrenciasEntrega from '../../Entrega/Ocorrencias/OcorrenciaEntrega/OcorrenciasEntrega'
import ModalMapCliente from '../../Cadastros/Cliente/ModalCliente/ModalMapCliente'

const STATUS_OPTIONS = [
  { label: 'Entregue', action: 'entrega_total', statusId: 8 },
  { label: 'Reentrega', action: 'reentrega', statusId: 12 },
  { label: 'Devolução Total', action: 'devolucao_total', statusId: 9 },
  { label: 'Devolução Parcial', action: 'devolucao_parcial', statusId: 10 },
  { label: 'Em Rota', action: 'em_rota', statusId: 13 },
  { label: 'No Cliente', action: 'no_cliente', statusId: 14 },
  { label: 'Descarregando', action: 'descarregando', statusId: 15 },
]

function ModalInformacoesEntrega({
  visible,
  delivery,
  onClose,
  onRefresh,
  onOcorrenciasVistas,
  dispatch,
}) {
  const [maximized, setMaximized] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [entrega, setEntrega] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(null)
  const [loadingEntrega, setLoadingEntrega] = useState(false)
  const [faltas, setFaltas] = useState([])
  const [loadingFaltas, setLoadingFaltas] = useState(false)
  const [faltasLoaded, setFaltasLoaded] = useState(false)
  const [cliente, setCliente] = useState(null)
  const [loadingCliente, setLoadingCliente] = useState(false)
  const [clienteLoaded, setClienteLoaded] = useState(false)
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0)

  const entregaAtual = entrega || delivery
  const rotaFinalizada = !!(delivery && delivery.returned_time_raw)

  const fetchEntrega = useCallback(async () => {
    if (!delivery || !delivery.delivery_id) return

    try {
      setLoadingEntrega(true)
      const fetchedEntrega = await getDelivery(delivery.delivery_id)
      setEntrega({ ...delivery, ...fetchedEntrega })
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
      setEntrega(delivery)
    } finally {
      setLoadingEntrega(false)
    }
  }, [delivery, dispatch])

  const fetchFaltas = useCallback(async () => {
    if (!delivery || !delivery.delivery_id) return

    try {
      setLoadingFaltas(true)
      const fetchedFaltas = await getMissingProductByDeliveryId(delivery.delivery_id)
      setFaltas(fetchedFaltas)
      setFaltasLoaded(true)
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    } finally {
      setLoadingFaltas(false)
    }
  }, [delivery, dispatch])

  const fetchCliente = useCallback(async () => {
    if (!delivery || !delivery.delivery_id) return

    try {
      setLoadingCliente(true)
      const fetchedCliente = await getClientByDeliveryId(delivery.delivery_id)
      setCliente(fetchedCliente)
      setClienteLoaded(true)
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    } finally {
      setLoadingCliente(false)
    }
  }, [delivery, dispatch])

  useEffect(() => {
    if (!visible || !delivery) return

    setMaximized(false)
    setActiveIndex(0)
    setEntrega(delivery)
    setSelectedStatus(null)
    setFaltas([])
    setFaltasLoaded(false)
    setCliente(null)
    setClienteLoaded(false)
    fetchEntrega()
  }, [visible, delivery, fetchEntrega])

  const onTabChange = index => {
    setActiveIndex(index)

    switch (index) {
      case 0:
      case 1:
        fetchEntrega()
        break
      case 2:
        fetchFaltas()
        break
      case 4:
        fetchCliente()
        break
      default:
        break
    }
  }

  const onSaveStatus = async () => {
    try {
      if (!selectedStatus) throw Error('Selecione um status para a Entrega')
      if (!delivery.tp_id) throw Error('TP da entrega não encontrado')

      await RetornoHttp.postEntregasAction(
        delivery.tp_id,
        [delivery.delivery_step_vehicle_id],
        selectedStatus.action,
      )

      setEntrega(prev => ({
        ...prev,
        status: selectedStatus.label,
        status_id: selectedStatus.statusId,
      }))
      setSelectedStatus(null)
      setTimelineRefreshKey(prev => prev + 1)
      dispatch(showMessageBox('Operação Realizada com Sucesso', null))
      if (onRefresh) await onRefresh()
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    }
  }

  const onUploadCanhotoCompleted = async fileName => {
    try {
      await RetornoHttp.putUpdateDeliveryPaths(delivery.delivery_id, 'canhoto', fileName)
      await fetchEntrega()
      if (onRefresh) await onRefresh()
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    }
  }

  const onDeleteCanhoto = async () => {
    try {
      if (!entregaAtual || !entregaAtual.path_canhoto) return

      const pathArray = entregaAtual.path_canhoto.split('/')
      const fileName = pathArray[pathArray.length - 1]

      await RetornoHttp.deleteDeliveryCanhotoFile(fileName)
      await RetornoHttp.putUpdateDeliveryPaths(delivery.delivery_id, 'canhoto', null)
      setEntrega(prev => ({ ...prev, path_canhoto: null }))
      if (onRefresh) await onRefresh()
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    }
  }

  const statusAtual = entregaAtual ? entregaAtual.status : ''
  const dialogStyle = maximized
    ? { width: '100vw', height: '100vh' }
    : { width: '88vw', height: '82vh' }

  return (
    <Dialog
      className="monitoramento-entrega-modal"
      header="Informações da Entrega"
      visible={visible}
      modal
      maximizable
      maximized={maximized}
      onMaximize={e => setMaximized(e.maximized)}
      onHide={onClose}
      style={dialogStyle}
      contentStyle={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
      }}
    >
      <div className="monitoramento-entrega-modal__content">
        <DeliveryInfoHeader entrega={entregaAtual} />

        <CustomTabView activeIndex={activeIndex} onTabChange={onTabChange}>
          <TabPanel header="Apuração Status">
            <StatusTab
              currentStatus={statusAtual}
              currentStatusId={entregaAtual ? entregaAtual.status_id : null}
              selectedStatus={selectedStatus}
              onChangeStatus={setSelectedStatus}
              onSaveStatus={onSaveStatus}
              rotaFinalizada={rotaFinalizada}
              deliveryId={delivery ? delivery.delivery_id : null}
              timelineRefreshKey={timelineRefreshKey}
              onTimelineError={error => dispatch(showMessageBox(null, error))}
            />
          </TabPanel>

          <TabPanel header="Canhoto">
            <CanhotoTab
              deliveryId={delivery ? delivery.delivery_id : null}
              entrega={entregaAtual}
              loading={loadingEntrega}
              onUploadCompleted={onUploadCanhotoCompleted}
              onDeleteCanhoto={onDeleteCanhoto}
            />
          </TabPanel>

          <TabPanel header="Ocorrências Produto">
            <OcorrenciasProdutoTab
              loading={loadingFaltas}
              loaded={faltasLoaded}
              faltas={faltas}
              setFaltas={setFaltas}
              fetchFaltas={fetchFaltas}
              dispatch={dispatch}
              delivery={delivery}
            />
          </TabPanel>

          <TabPanel header="Ocorrências Entrega">
            <OcorrenciasEntrega
              deliveryStepVehicleId={delivery ? delivery.delivery_step_vehicle_id : null}
              fleetId={delivery ? delivery.fleet_id : null}
              onOcorrenciasVistas={onOcorrenciasVistas}
            />
          </TabPanel>

          <TabPanel header="Dados do Cliente">
            <DadosClienteTab
              loading={loadingCliente}
              loaded={clienteLoaded}
              cliente={cliente}
              onLoad={fetchCliente}
            />
          </TabPanel>
        </CustomTabView>
      </div>
    </Dialog>
  )
}

function DeliveryInfoHeader({ entrega }) {
  const grupo = entrega && entrega.company_group_name ? String(entrega.company_group_name).trim() : ''

  return (
    <div className="monitoramento-entrega-modal__delivery-header">
      <div className="monitoramento-entrega-modal__delivery-info">
        <strong>Identificador:</strong> {formatHeaderValue(entrega && entrega.identifier)}
      </div>
      {grupo ? (
        <div className="monitoramento-entrega-modal__delivery-info">
          <strong>Grupo:</strong> {grupo}
        </div>
      ) : null}
      <div className="monitoramento-entrega-modal__delivery-info">
        <strong>Empresa:</strong> {formatHeaderValue(entrega && entrega.company_name)}
      </div>
      <div className="monitoramento-entrega-modal__delivery-info">
        <strong>Peso:</strong> {formatNumberValue(entrega && entrega.weightTotal)}
      </div>
      <div className="monitoramento-entrega-modal__delivery-info">
        <strong>Qte. Sku:</strong> {formatNumberValue(entrega && entrega.qteSkuTotal)}
      </div>
    </div>
  )
}

function StatusTab({
  currentStatus,
  currentStatusId,
  selectedStatus,
  onChangeStatus,
  onSaveStatus,
  rotaFinalizada,
  deliveryId,
  timelineRefreshKey,
  onTimelineError,
}) {
  return (
    <div className="monitoramento-entrega-modal__status-split">
      <div className="monitoramento-entrega-modal__status-tab">
        <div className="monitoramento-entrega-modal__status-current">
          <span>Status Atual:</span>
          <BadgeStatusEntrega status={currentStatus} />
        </div>

        <div className="monitoramento-entrega-modal__section-label">Alterar Status da Entrega</div>

        <div className="monitoramento-entrega-modal__status-options">
          {STATUS_OPTIONS.map(option => {
            const isCurrent =
              Number(currentStatusId) === option.statusId || currentStatus === option.label
            return (
              <label
                key={option.action}
                className={
                  isCurrent
                    ? 'monitoramento-entrega-modal__status-option monitoramento-entrega-modal__status-option--disabled'
                    : 'monitoramento-entrega-modal__status-option'
                }
              >
                <RadioButton
                  inputId={`status-${option.action}`}
                  name="delivery-status"
                  value={option}
                  checked={selectedStatus && selectedStatus.action === option.action}
                  disabled={isCurrent || rotaFinalizada}
                  onChange={e => onChangeStatus(e.value)}
                />
                <BadgeStatusEntrega status={option.label} />
              </label>
            )
          })}
        </div>

        {rotaFinalizada ? (
          <div className="monitoramento-entrega-modal__blocked-message">
            Essa rota já foi finalizada, não é possível alterar o status da Entrega
          </div>
        ) : (
          <div>
            <Btn minWidth={null} onClick={onSaveStatus}>
              Salvar
            </Btn>
          </div>
        )}
      </div>

      <div className="monitoramento-entrega-modal__timeline-pane">
        <div className="monitoramento-entrega-modal__section-label">Timeline</div>
        <TimelineSection
          deliveryId={deliveryId}
          refreshKey={timelineRefreshKey}
          onError={onTimelineError}
        />
      </div>
    </div>
  )
}

function TimelineSection({ deliveryId, refreshKey, onError }) {
  // Hooks
  const [isLoading, setIsLoading] = useState(false)
  const [eventos, setEventos] = useState([])
  useEffect(() => {
    getEventos()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryId, refreshKey])

  //Events
  const getEventos = async () => {
    if (!deliveryId) {
      setEventos([])
      return
    }

    try {
      setIsLoading(true)
      const fetchedEventos = await getDeliveryTimeline(deliveryId)
      setEventos(fetchedEventos)
    } catch (e) {
      onError(errorHandler(e))
    } finally {
      setIsLoading(false)
    }
  }

  // Rendering
  if (isLoading) {
    return (
      <div className="monitoramento-entrega-modal__loading">
        <Loading />
      </div>
    )
  }

  if (!eventos.length) {
    return <div className="monitoramento-entrega-modal__empty-state">Nenhum evento encontrado</div>
  }

  return (
    <div className="monitoramento-entrega-modal__timeline-list">
      {eventos.map((evento, index) => {
        const { status, created_at, description, tp, user, motivo_atraso } = evento
        return (
          <Card key={index} style={{ paddingTop: 10, paddingBottom: 10 }}>
            <div style={{ display: 'flex' }}>
              <div
                style={{
                  width: 150,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <BadgeStatusEntrega status={status} />
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: vinho, fontStyle: 'italic' }}>
                  {DateTimeLuxon.fromISO(created_at).toFormat('dd/MM/yyyy HH:mm')}
                </div>
                <div>
                  {description} {motivo_atraso ? ` - ${motivo_atraso}` : ''}
                </div>
                <div style={{ fontSize: '0.7rem', color: azul_escuro }}>{tp}</div>
                <div style={{ fontSize: '0.7rem', color: '#c4c4c4', fontStyle: 'italic' }}>
                  {user}
                </div>
              </div>
            </div>
          </Card>
        )
      })}
    </div>
  )
}

function CanhotoTab({ deliveryId, entrega, loading, onUploadCompleted, onDeleteCanhoto }) {
  return (
    <div className="monitoramento-entrega-modal__canhoto-tab">
      {!loading && !(entrega && entrega.path_canhoto) && (
        <div className="monitoramento-entrega-modal__upload-slot">
          <UploadFileBtn
            label="Anexar Canhoto"
            name="canhoto"
            url={urlUploadCanhoto(deliveryId)}
            onUploadCompleted={onUploadCompleted}
          />
        </div>
      )}

      {loading ? (
        <CenteredLoading />
      ) : entrega && entrega.path_canhoto ? (
        <ImageContainer
          imagePath={entrega.path_canhoto}
          alt="Canhoto"
          onBtnDeleteClicked={onDeleteCanhoto}
        />
      ) : (
        <div className="monitoramento-entrega-modal__empty-state">
          Nenhum canhoto anexado para esta entrega
        </div>
      )}
    </div>
  )
}

function OcorrenciasProdutoTab({
  loading,
  loaded,
  faltas,
  setFaltas,
  fetchFaltas,
  dispatch,
  delivery,
}) {
  useEffect(() => {
    if (!loaded && !loading) fetchFaltas()
  }, [fetchFaltas, loaded, loading])

  if (loading || !loaded) return <CenteredLoading />

  return (
    <FaltaDataTable
      ocorrencias={faltas}
      setOcorrencias={setFaltas}
      getEntregaComOcorrencias={fetchFaltas}
      dispatch={dispatch}
      delivery_id={delivery ? delivery.delivery_id : null}
      delivery_step_id={delivery ? delivery.delivery_step_id : null}
      nomeFantasia={delivery ? delivery.nomeFantasia : ''}
      identifier={delivery ? delivery.identifier : ''}
      modalContentClassName="monitoramento-entrega-modal__inner-modal-content monitoramento-entrega-modal__falta-modal-content"
      modalStyle={{
        width: 'min(1180px, 94vw)',
        maxHeight: '88vh',
        overflowY: 'auto',
        backgroundColor: 'white',
      }}
    />
  )
}

function DadosClienteTab({ loading, loaded, cliente, onLoad }) {
  const [activeClienteTab, setActiveClienteTab] = useState(0)
  const coordinates = useMemo(
    () => ({
      longitude: Number(cliente && cliente.longitude) || 0,
      latitude: Number(cliente && cliente.latitude) || 0,
    }),
    [cliente],
  )

  useEffect(() => {
    setActiveClienteTab(0)
  }, [cliente])

  useEffect(() => {
    if (!loaded && !loading) onLoad()
  }, [loaded, loading, onLoad])

  if (loading || !loaded) return <CenteredLoading />
  if (!cliente) {
    return <div className="monitoramento-entrega-modal__empty-state">Cliente não encontrado</div>
  }

  return (
    <CustomTabView activeIndex={activeClienteTab} onTabChange={setActiveClienteTab}>
      <TabPanel header="Dados">
        <div className="monitoramento-entrega-modal__cliente-grid">
          <InputText label="CNPJ/CPF :" value={cliente.cnpj || ''} disabled marginTop={10} />
          <InputText label="Telefone :" value={cliente.phone || ''} disabled marginTop={10} />
          <InputText label="Estado :" value={cliente.state || ''} disabled marginTop={10} />
          <InputText label="Cidade :" value={cliente.city || ''} disabled marginTop={10} />
          <InputText
            label="Razão Social :"
            value={cliente.razaoSocial || ''}
            disabled
            marginTop={10}
          />
          <InputText
            label="Nome Fantasia :"
            value={cliente.nomeFantasia || ''}
            disabled
            marginTop={10}
          />
          <InputText label="Endereço :" value={cliente.address || ''} disabled marginTop={10} />
          <InputText label="Bairro :" value={cliente.district || ''} disabled marginTop={10} />
          <InputText label="Número :" value={cliente.number || ''} disabled marginTop={10} />
          <InputText label="CEP :" value={cliente.cep || ''} disabled marginTop={10} />
          <InputText
            label="Inscrição Estadual :"
            value={cliente.inscricao_estadual || ''}
            disabled
            marginTop={10}
          />
          <InputText
            label="Coordenadas :"
            value={`${cliente.latitude || ''}${cliente.longitude ? `, ${cliente.longitude}` : ''}`}
            disabled
            marginTop={10}
          />
        </div>
      </TabPanel>
      <TabPanel header="Mapa">
        <ModalMapCliente
          coordinates={coordinates}
          setCoordinates={() => {}}
          onSave={() => {}}
          readOnly
        />
      </TabPanel>
      <TabPanel header="Observações">
        <CustomInputTextArea
          label="Observações"
          value={cliente.observation || ''}
          onChange={() => {}}
          rows={6}
          disabled
        />
      </TabPanel>
    </CustomTabView>
  )
}

function CenteredLoading() {
  return (
    <div className="monitoramento-entrega-modal__loading">
      <Loading />
    </div>
  )
}

function formatHeaderValue(value) {
  if (value === null || value === undefined || value === '') return '-'
  return value
}

function formatNumberValue(value) {
  if (value === null || value === undefined || value === '') return '-'
  const numericValue = Number(value)
  if (Number.isNaN(numericValue)) return value
  return numericValue.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })
}

export default connect()(ModalInformacoesEntrega)
